import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getSortedColors, isSizeAvailableForSelectedColor } from "./utils";
import { QVMContext } from "./quick-view-modal.ctx";
// The following dep needs to be moved to v3 later.
import usePrevious from "@lululemon/ecom-core/src/hooks/use-previous";

export const useQVMContext = () => {
  return useContext(QVMContext);
};

/**
 * Hook to format and provide controls to modify the
 * data of the QuickViewModal
 * @param {*} config
 * @returns
 */
export const useQuickViewModal = (config) => {
  const {
    data: productData,
    inventoryData,
    events = {},
    defaultColorCode,
  } = config || {};
  const {
    allSize,
    productCarousel = [],
    productSummary = {},
    skus = [],
    variants = [],
  } = productData || {};
  const sortedColors = getSortedColors({
    productCarousel,
    skus,
  });
  const [modalError, setModalError] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(null);
  const [selectedSizeIdx, setSelectedSizeIdx] = useState(null);

  const selectedColor = sortedColors?.[selectedColorIndex];
  const selectedVariant =
    variants?.find((prod) => prod.color.code === selectedColor?.code) ??
    variants?.[0];

  /**
   * Only sizes that associated to the current colors will be listed.
   */
  const sizes = useMemo(() => {
    return allSize
      ?.map((sizeObj) => sizeObj.size)
      .filter((size) =>
        skus.some(
          ({ color, size: s }) =>
            color.code === selectedColor?.code && size === s
        )
      );
  }, [allSize, skus, selectedColor]);

  const selectedSize = sizes?.[selectedSizeIdx];
  const selectedSku = skus?.find(({ color, size }) => {
    return selectedSize === size && selectedColor?.code == color.code;
  });
  const selectedInventoryDetails = inventoryData?.find(
    (sku) => sku?.id === selectedSku?.id
  );

  /**
   * This object needs to be aware of changes on the variant and
   * skus
   */
  const buildEventInfo = useMemo(() => {
    return {
      selectedVariant,
      skus,
      product: {
        name: productSummary?.displayName || productSummary?.title,
      },
    };
  }, [skus, productSummary, selectedVariant]);

  /**
   * Works as a proxy to provide useful data to the outside events.
   * @param {*} colorIndex
   * @param {*} shouldTrigger To allow trigger this event without notifying outside consumers
   */
  const handleChangeColor = (colorIndex, shouldTrigger = true) => {
    setSelectedColorIndex(colorIndex);
    /**
     * Making sure that for the event we pass the selected color and
     * it's corresponding sku
     */
    const newSelectedColor = sortedColors?.[colorIndex];
    shouldTrigger &&
      events?.onColorChange?.({
        ...buildEventInfo,
        selectedColor: newSelectedColor,
        selectedSize,
        selectedSku: skus?.find(({ color, size }) => {
          return selectedSize === size && newSelectedColor?.code == color.code;
        }),
      });
  };

  /**
   * Works as a proxy to provide useful data to the outside events.
   * @param {*} sizeIndex
   * @param {*} shouldTrigger To allow trigger this event without notifying outside consumers
   */
  const handleChangeSize = (sizeIndex, shouldTrigger = true) => {
    setSelectedSizeIdx(sizeIndex);
    /**
     * Making sure that for the event we pass the selected size and
     * it's corresponding sku
     */
    const newSelectedSize = sizes?.[sizeIndex];
    shouldTrigger &&
      events?.onSizeChange({
        ...buildEventInfo,
        selectedSize: newSelectedSize,
        selectedColor,
        selectedSku: skus?.find(({ color, size }) => {
          return newSelectedSize === size && selectedColor?.code == color.code;
        }),
      });
  };

  /**
   * The default color index needs to be selected via useEffect due to
   * on the initial load there is no colors provided yet.
   */
  useEffect(() => {
    if (
      selectedColorIndex === null &&
      sortedColors?.length &&
      defaultColorCode
    ) {
      const defaultColorIndex = sortedColors?.findIndex(
        ({ code }) => code === defaultColorCode
      );
      setSelectedColorIndex(defaultColorIndex >= 0 ? defaultColorIndex : 0);
    }
  }, [sortedColors, defaultColorCode, selectedColorIndex]);

  const handleAddToBag = useCallback(() => {
    events?.onAddToBag?.({
      color: selectedColor,
      size: selectedSize,
      sku: selectedSku,
      skus,
      sizes,
      selectedVariant,
      productSummary,
    });
  }, [
    selectedColor,
    selectedSize,
    selectedSku,
    skus,
    sizes,
    selectedVariant,
    productSummary,
  ]);

  const prevSelectedSize = usePrevious(selectedSize);
  const prevSelectedColorIdx = usePrevious(selectedColorIndex);

  useEffect(() => {
    // Changing the color index may unintentionally change the selected size
    // because two different colors may have two different sets of available sizes
    // and we don't store the selectedSize in state (just the _selectedSizeIdx_)
    const hasColorChanged = selectedColorIndex !== prevSelectedColorIdx;
    const isSizeAvailable = isSizeAvailableForSelectedColor({
      skus,
      size: prevSelectedSize,
      selectedColor,
    });
    if (hasColorChanged && isSizeAvailable) {
      const idx = sizes.findIndex((s) => s === prevSelectedSize);
      if (idx !== -1) {
        setSelectedSizeIdx(idx);
      }
    }
  }, [
    selectedColorIndex,
    prevSelectedColorIdx,
    prevSelectedSize,
    selectedColor,
    setSelectedSizeIdx,
    sizes,
    isSizeAvailableForSelectedColor,
    skus,
  ]);

  useEffect(() => {
    if (
      selectedSize &&
      !isSizeAvailableForSelectedColor({
        skus,
        size: selectedSize,
        selectedColor,
      })
    ) {
      setModalError(new Error("Sold out online."));
    } else {
      setModalError(null);
    }
  }, [
    selectedSize,
    isSizeAvailableForSelectedColor,
    sizes,
    selectedColor,
    skus,
  ]);

  return {
    modalError,
    productSummary,
    sizes,
    skus,
    sortedColors,
    selectedColor,
    selectedColorIndex,
    selectedSize,
    selectedSizeIdx,
    selectedSku,
    selectedVariant,
    selectedInventoryDetails,
    setSelectedColorIndex: handleChangeColor,
    setSelectedSizeIdx: handleChangeSize,
    handleAddToBag,
  };
};

/**
 * Hook to handle the states and provide the
 * carousel actions
 * @param {*} imageSet
 * @param {*} onSlideChangeEvent
 * @returns
 */
export const useCarouselActions = (config) => {
  const { images, events = {} } = config || {};
  const [activeSlide, setActiveSlide] = useState(0);

  const handleSlideChange = (slideIndex) => {
    const totalCount = images?.length;
    setActiveSlide(slideIndex);
    events?.onSlideChange?.({
      totalCount,
      position: slideIndex,
    });
  };

  const sanitizedActiveSlide = activeSlide < images?.length ? activeSlide : 0;

  return {
    handleSlideChange,
    activeSlide: sanitizedActiveSlide,
  };
};
